import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Avatar from './Avatar';
import VoicePlayer from './VoicePlayer';
import MediaLightbox from './MediaLightbox';
import { formatDistanceToNow, format } from 'date-fns';
import { vi, enUS } from 'date-fns/locale';
import { chatApi } from '../services/chatApi';

import {
  IoCheckmark,
  IoCheckmarkDone,
  IoReturnUpBack,
  IoCreateOutline,
  IoTrashOutline,
  IoHeartOutline,
  IoLinkOutline,
} from 'react-icons/io5';
import { FaHeart, FaLaughBeam, FaSadTear, FaAngry } from 'react-icons/fa';
import './MessageItem.css';

// Helper to validate URL and prevent XSS attacks via javascript: or data: URLs
const isValidURL = (url) => {
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

// Helper to render message content with clickable URLs and chat links
const renderMessageContent = (content, onNavigateToMessage) => {
  if (!content) return null;

  // URL pattern: http/https links
  const urlRegex = /(https?:\/\/[^\s<]+)/g;

  // Combine patterns and track positions
  const matches = [];
  let match;

  // Find all URL matches
  while ((match = urlRegex.exec(content)) !== null) {
    const url = match[0];
    // Check if this URL contains a chat link path
    const chatLinkMatch = url.match(/\/chat-link\/([a-zA-Z0-9-]+)/);

    if (chatLinkMatch) {
      // Full chat link URL - treat as internal navigation
      matches.push({
        index: match.index,
        length: url.length,
        text: url,
        type: 'chat',
        shortCode: chatLinkMatch[1],
      });
    } else if (isValidURL(url)) {
      // Regular URL - only add if valid (blocks javascript:, data:, etc.)
      matches.push({
        index: match.index,
        length: url.length,
        text: url,
        type: 'url',
      });
    }
  }

  // Also match relative chat links (without hostname)
  const chatLinkRegex = /\/chat-link\/([a-zA-Z0-9-]+)/g;
  while ((match = chatLinkRegex.exec(content)) !== null) {
    // Check if this position is already covered by a URL match
    const alreadyMatched = matches.some(
      m => match.index >= m.index && match.index < m.index + m.length
    );
    if (!alreadyMatched) {
      matches.push({
        index: match.index,
        length: match[0].length,
        text: match[0],
        type: 'chat',
        shortCode: match[1],
      });
    }
  }

  // Sort by position
  matches.sort((a, b) => a.index - b.index);

  // Build parts array
  const parts = [];
  let lastIndex = 0;

  for (const m of matches) {
    // Skip overlapping matches
    if (m.index < lastIndex) continue;

    // Add text before the link
    if (m.index > lastIndex) {
      parts.push(content.substring(lastIndex, m.index));
    }

    // Add the link
    if (m.type === 'chat') {
      parts.push(
        <span
          key={m.index}
          className="message-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNavigateToMessage(m.shortCode);
          }}
        >
          {m.text}
        </span>
      );
    } else {
      // Regular URL - open in new tab
      parts.push(
        <a
          key={m.index}
          href={m.text}
          target="_blank"
          rel="noopener noreferrer"
          className="message-link"
          onClick={(e) => e.stopPropagation()}
        >
          {m.text}
        </a>
      );
    }

    lastIndex = m.index + m.length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts.length > 0 ? parts : content;
};

const REACTION_EMOJIS = {
  like: <span key="like">👍</span>,
  love: <FaHeart key="love" color="#e0245e" />,
  laugh: <FaLaughBeam key="laugh" color="#f5a623" />,
  cry: <FaSadTear key="cry" color="#1da1f2" />,
  angry: <FaAngry key="angry" color="#e0245e" />,
  wow: <span key="wow">😮</span>,
};

const REACTION_LIST = ['like', 'love', 'laugh', 'cry', 'angry', 'wow'];

const MessageItem = ({ message, isOwn, showAvatar, currentUserId, conversationId, onReply, onEdit, onDelete, onReact, onJumpToMessage, onRetry, onDeleteFailed }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showActions, setShowActions] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const dateLocale = i18n.language === 'vi' ? vi : enUS;
  const reactionPickerRef = useRef(null);
  const actionButtonsRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const messageRef = useRef(null);

  // Message status: 'sending', 'sent', 'failed'
  const messageStatus = message.status || 'sent';

  // Handle clicking on a chat link within message content
  const handleNavigateToMessage = (shortCode) => {
    navigate(`/chat-link/${shortCode}`);
  };

  // Expose scroll-to-me functionality via callback
  useEffect(() => {
    if (messageRef.current && onJumpToMessage) {
      // Store the element in a map for parent to access
      messageRef.current.dataset.messageId = message.id;
    }
  }, [message.id, onJumpToMessage]);

  // Find the current user's reaction if any
  const userReaction = message.reactions?.find(r => r.user_id === currentUserId);

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target)) {
        setShowReactionPicker(false);
      }
    };

    if (showReactionPicker) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showReactionPicker]);

  // Handle mouse leave with delay to allow moving to picker
  const handleActionsMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowReactionPicker(false);
    }, 400);
  };

  const handleActionsMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const hasBeenRead = message.read_by && message.read_by.length > 0;

  const handleStartEdit = () => {
    setEditContent(message.content || '');
    setEditing(true);
    setShowActions(false);
  };

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent.trim() !== message.content) {
      onEdit(message.id, editContent.trim());
    }
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditContent('');
  };

  const handleDelete = () => {
    if (window.confirm(t('chat.confirmDelete'))) {
      onDelete(message.id);
    }
    setShowActions(false);
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    let messageLink;

    try {
      // Try to create a short link via API
      const response = await chatApi.createMessageLink(message.id);
      if (response.success) {
        const shortCode = response.data.short_code;
        // Use full URL for clipboard (external sharing)
        messageLink = `${baseUrl}/chat-link/${shortCode}`;
      } else {
        // API returned error, use UUID format
        messageLink = `${baseUrl}/chat-link/${message.id}`;
      }
    } catch (err) {
      console.error('Failed to create short link:', err);
      // Fallback to UUID format if API fails
      messageLink = `${baseUrl}/chat-link/${message.id}`;
    }

    // Use fallback clipboard method
    try {
      await navigator.clipboard.writeText(messageLink);
    } catch (clipErr) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = messageLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    setShowActions(false);
  };

  return (
    <div
      ref={messageRef}
      id={`message-${message.id}`}
      className={`message-item ${isOwn ? 'own' : 'other'} ${showAvatar ? 'with-avatar' : ''} ${messageStatus}`}
      onMouseEnter={() => !editing && setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {!isOwn && showAvatar && (
        <div className="message-avatar">
          <Avatar user={message.user} size="small" />
        </div>
      )}
      {!isOwn && !showAvatar && <div className="message-avatar-spacer" />}

      <div className="message-content-wrapper">
        {!isOwn && showAvatar && (
          <Link to={`/profile/${message.user?.id}`} className="message-author">
            {message.user?.display_name}
          </Link>
        )}

        {message.reply_to_message && (
          <div
            className="message-reply-preview"
            onClick={() => onJumpToMessage && onJumpToMessage(message.reply_to_message.id)}
          >
            <span className="reply-author">{message.reply_to_message.user?.display_name}</span>
            <span className="reply-text">
              {message.reply_to_message.is_deleted
                ? t('chat.messageDeleted')
                : message.reply_to_message.voice_url
                  ? `🎤 ${t('chat.voiceMessage')}`
                  : message.reply_to_message.media_urls?.length > 0
                    ? `📷 ${t('chat.image')}`
                    : (message.reply_to_message.content || '').substring(0, 80)}
            </span>
          </div>
        )}

        <div className={`message-bubble ${(message.media_urls?.length > 0 || message.voice_url) && !message.is_deleted ? 'has-media' : ''}`}>
          {message.is_deleted ? (
            <span className="deleted-text">{t('chat.messageDeleted')}</span>
          ) : editing ? (
            <div className="message-edit-form">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveEdit();
                  }
                  if (e.key === 'Escape') handleCancelEdit();
                }}
              />
              <div className="edit-actions">
                <button onClick={handleCancelEdit} className="edit-cancel">{t('common.cancel')}</button>
                <button onClick={handleSaveEdit} className="edit-save">{t('common.save')}</button>
              </div>
            </div>
          ) : (
            <>
              {/* Media grid */}
              {message.media_urls && message.media_urls.length > 0 && (
                <div className={`msg-media-grid grid-${Math.min(message.media_urls.length, 4)}`}>
                  {message.media_urls.map((url, idx) => (
                    <div key={idx} className="msg-media-item" onClick={() => message.status === 'sent' && setLightboxIndex(idx)}>
                      {message.media_types?.[idx] === 'video' ? (
                        <video src={url} preload="metadata" playsInline />
                      ) : (
                        <img src={url} alt="" />
                      )}
                      {message.status === 'sending' && message.uploadProgress !== undefined && (
                        <div className="media-upload-overlay">
                          <div className="media-upload-progress">
                            <div
                              className="media-upload-progress-bar"
                              style={{ width: `${message.uploadProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Voice player */}
              {message.voice_url && (
                <div className="msg-voice">
                  <VoicePlayer
                    voiceUrl={message.voice_url}
                    duration={message.voice_duration}
                    compact
                    dark={true}
                    waveColor={isOwn ? 'rgba(255,255,255,0.4)' : undefined}
                    progressColor={isOwn ? '#ffffff' : undefined}
                  />
                  {message.status === 'sending' && message.uploadProgress !== undefined && (
                    <div className="voice-upload-progress">
                      <div
                        className="voice-upload-progress-bar"
                        style={{ width: `${message.uploadProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Text content with clickable links */}
              {message.content && (
                <span className="message-text">
                  {renderMessageContent(message.content, handleNavigateToMessage)}
                </span>
              )}
            </>
          )}
        </div>

        <div className="message-meta">
          <span className="message-time" title={format(new Date(message.created_at), 'PPpp', { locale: dateLocale })}>
            {formatDistanceToNow(new Date(message.created_at), {
              addSuffix: true,
              locale: dateLocale,
            })}
          </span>
          {message.is_edited && !message.is_deleted && (
            <span className="edited-label">{t('chat.edited')}</span>
          )}
          {isOwn && !message.is_deleted && (
            <span className="read-indicator">
              {hasBeenRead ? <IoCheckmarkDone /> : <IoCheckmark />}
            </span>
          )}
          {/* Reactions display - inline with meta (after time for others, after read indicator for own) */}
          {message.reactions && message.reactions.length > 0 && (
            <div className="message-reactions">
              {message.reactions.slice(0, 3).map((r, idx) => (
                <span key={idx} className="reaction-emoji">
                  {REACTION_EMOJIS[r.reaction]}
                </span>
              ))}
              {message.reactions.length > 3 && (
                <span className="reaction-count">+{message.reactions.length - 3}</span>
              )}
            </div>
          )}
        </div>

        {/* Status indicator for optimistic messages */}
        {messageStatus === 'sending' && (
          <div className="message-status sending">
            <span className="sending-spinner" />
          </div>
        )}
        {messageStatus === 'failed' && isOwn && (
          <div className="message-status failed">
            <span className="failed-icon" title={t('chat.sendFailed')}>⚠</span>
            <button
              className="retry-btn"
              onClick={() => onRetry && onRetry(conversationId, message.id)}
            >
              {t('common.retry')}
            </button>
            <button
              className="delete-btn"
              onClick={() => onDeleteFailed && onDeleteFailed(conversationId, message.id)}
            >
              {t('common.delete')}
            </button>
          </div>
        )}

        {/* Action buttons on hover */}
        {showActions && !message.is_deleted && !editing && (
          <div
            ref={actionButtonsRef}
            className={`message-actions ${isOwn ? 'own' : 'other'}`}
            onMouseLeave={handleActionsMouseLeave}
            onMouseEnter={handleActionsMouseEnter}
          >
            <button
              onClick={() => onReact(message.id, 'love')}
              onMouseEnter={() => setShowReactionPicker(true)}
              data-tooltip={t('chat.react')}
            >
              <IoHeartOutline />
            </button>
            <button onClick={onReply} data-tooltip={t('chat.reply')}>
              <IoReturnUpBack />
            </button>
            <button onClick={handleCopyLink} data-tooltip={t('chat.copyLink') || 'Copy link'}>
              <IoLinkOutline />
            </button>
            {isOwn && (
              <>
                <button onClick={handleStartEdit} data-tooltip={t('common.edit')}>
                  <IoCreateOutline />
                </button>
                <button onClick={handleDelete} data-tooltip={t('common.delete')}>
                  <IoTrashOutline />
                </button>
              </>
            )}
          </div>
        )}

        {/* Reaction picker */}
        {showReactionPicker && (
          <div
            className={`reaction-picker ${isOwn ? 'own' : 'other'}`}
            ref={reactionPickerRef}
            onMouseEnter={handleActionsMouseEnter}
            onMouseLeave={handleActionsMouseLeave}
          >
            {REACTION_LIST.map(r => {
              const isUserReaction = userReaction?.reaction === r;
              return (
                <button
                  key={r}
                  className={`reaction-btn ${isUserReaction ? 'active' : ''}`}
                  onClick={() => {
                    if (isUserReaction) {
                      // Remove reaction if already reacted with this emoji
                      onReact(message.id, r);
                    } else {
                      // Add or change reaction
                      onReact(message.id, r);
                    }
                    setShowReactionPicker(false);
                  }}
                >
                  {REACTION_EMOJIS[r]}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {lightboxIndex !== null && message.media_urls && (
        <MediaLightbox
          mediaUrls={message.media_urls}
          mediaTypes={message.media_types}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

export default MessageItem;
